import {api} from './api'

export interface IGetTotalEntriesResponse {
  count: number
}

export interface PrizePeriod {
  id: string
  name: string
  start_date: string
  end_date: string
}

export interface IGetPrizePeriodsResponse {
  prize_periods: PrizePeriod[]
}

export interface ICustomer {
  id: string
  cognito_id: string
  cognito_created_at: string
  cognito_verified: boolean
  name: string
  email: string
  phone: string
  orders_count: number
  created_at: string
  updated_at: string
  address_1: string
  address_2: string | null
  city: string
  country: string
  first_name: string
  last_name: string
  shopify_customer_id: string
  state: string
  zip_code: string
}

export interface IGetCustomerOrdersResponse {
  customer_orders: ICustomerOrders[]
  records_count: number
  next_page: number
  previous_page: number
}
export interface ICustomerOrders {
  created_at: string
  currency: string
  id: string
  order_date: string
  order_number: string
  source_order_id: string
  total_discount: number
  total_price: number
  updated_at: string
}

export interface IGetCustomerClassesResponse {
  customer_classes: ICustomerClasses[]
  records_count: number
  next_page: number
  previous_page: number
}
export interface ICustomerClasses {
  created_at: string
  date_end: string
  date_start: string
  id: string
  location: ICustomerClassLocation
  order: ICustomerClassOrder
  class: ICustomerClassData
  status: string
  updated_at: string
  certification: ICustomerClassCertification | null
  order_line_item_id: string
}
export interface ICustomerClassLocation {
  id: string,
  name: string,
  timezone: string
}

export interface ICustomerClassOrder {
  id: string
  order_number: string
}

export interface ICustomerClassData {
  id: string
  name: string
}

export interface ICustomerClassCertification {
  id: string
  customer_id: string
  class_id: string
  order_line_item_id: string
  name?: string
  certification_number: string
  certification_link: string
  created_at: string
  updated_at: string
}

export interface IGetCustomerCertificationsResponse {
  customer_certifications: ICustomerCertifications[]
  records_count: number
  next_page: number
  previous_page: number
}

export interface ICustomerCertifications {
  id: string
  customer_id: string,
  class_id: string,
  name: string,
  certification_number: string,
  certification_link: string,
  created_at: string,
  updated_at: string
}

export interface IGetCustomersResponse {
  customers: ICustomer[]
  records_count: number
  next_page: number
  previous_page: number
}

export interface PaginationParams {
  limit: number
  page: number
}

export interface IEntry {
  id: string
  prize_period_id: string
  order_id: string
  admin_id: string
  number_of_entries: number
  email: string
  phone: string
  created_at: string
  updated_at: string
}

export interface IGetCustomerEntriesResponse {
  entries: IEntry[]
  records_count: number
  next_page: number
  previous_page: number
}

export const customersApi = api.injectEndpoints({
  endpoints: (build) => ({
    getCustomers: build.query<IGetCustomersResponse, any>({
      query: ({ limit, page, startDate, endDate }) => {
        let url = `/customers?limit=${limit}&page=${page}`;
        if (startDate && endDate) {
          url += `&filter_by=created_at:">=${startDate}" created_at:"<=${endDate}"`;
        }
        return { url, method: 'GET' };
      },
      providesTags: ['Customers']
    }),
    getCustomer: build.query<{ customer: ICustomer }, string>({
      query: (customerId) => {
        return { url: `/customers/${customerId}`, method: 'GET' }
      }
    }),
    getCustomerOrders: build.query<IGetCustomerOrdersResponse, string>({
      query: (customerId) => {
        return { url: `/customers/${customerId}/orders`, method: 'GET' }
      }
    }),
    getCustomerClasses: build.query<IGetCustomerClassesResponse, string>({
      query: (customerId) => {
        return { url: `/customers/${customerId}/classes`, method: 'GET' }
      }
    }),
    getCustomerCertifications: build.query<IGetCustomerCertificationsResponse, string>({
      query: (customerId) => {
        return { url: `/customers/${customerId}/certifications`, method: 'GET' }
      }
    }),
    getCustomerEntries: build.query<
      IGetCustomerEntriesResponse,
      { customerId: string; limit: number; page: number }
    >({
      query: ({ customerId, page, limit }) => {
        return {
          url: `/customers/${customerId}/entries?page=${page}&limit=${limit}`,
          method: 'GET'
        }
      },
      providesTags: ['Entries']
    }),
    getTotalEntries: build.query<IGetTotalEntriesResponse, string>({
      query: (customerId) => {
        return { url: `/customers/${customerId}/entries/count`, method: 'GET' }
      }
    }),
    updateCustomer: build.mutation<any, { customerId: string; body: object }>({
      query: ({ customerId, body }) => ({
        url: `/customers/${customerId}`,
        method: 'PUT',
        body
      })
    }),
    addCustomerEntries: build.mutation<
      any,
      { customerId: string; entries: number }
    >({
      query: ({ customerId, entries }) => ({
        url: `/customers/${customerId}/entries`,
        method: 'POST',
        body: {
          number_of_entries: entries
        }
      }),
      invalidatesTags: ['Entries']
    })
  })
})

export const {
  useGetTotalEntriesQuery,
  useGetCustomerEntriesQuery,
  useGetCustomersQuery,
  useGetCustomerQuery,
  useAddCustomerEntriesMutation,
  useUpdateCustomerMutation,
  useGetCustomerOrdersQuery,
  useGetCustomerClassesQuery,
  useGetCustomerCertificationsQuery
} = customersApi
